import { useEffect, useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useI18n } from '@/contexts/I18nContext';
import { formatCurrency } from '@/lib/formatters';
import { Wallet, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DriverCodSettlement,
  DriverPaySettlement,
} from '@/types/financial.types';

interface DriverBalanceCardProps {
  driverId: string;
}

function extractList(result: any): any[] {
  const data = result?.success !== undefined ? result?.data : result;
  if (Array.isArray(data)) return data;
  return data?.list ?? data?.data ?? [];
}

export function DriverBalanceCard({ driverId }: DriverBalanceCardProps) {
  const { t } = useI18n();
  const codApi = useApi({ showErrorToast: false });
  const payApi = useApi({ showErrorToast: false });
  const [unsettledCod, setUnsettledCod] = useState<number>(0);
  const [unsettledPay, setUnsettledPay] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!driverId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      codApi.get(`/api/v1/driver-cod-settlements?driver_id=${driverId}`).catch(() => null),
      payApi.get(`/api/v1/driver-pay-settlements?driver_id=${driverId}`).catch(() => null),
    ]).then(([codRes, payRes]) => {
      if (cancelled) return;
      const codList: DriverCodSettlement[] = extractList(codRes);
      const payList: DriverPaySettlement[] = extractList(payRes);
      const codSum = codList
        .filter((s) => s.status === 'draft' || s.status === 'confirmed')
        .reduce((sum, s) => sum + Number(s.total_amount || 0), 0);
      const paySum = payList
        .filter((s) => s.status === 'draft' || s.status === 'confirmed')
        .reduce((sum, s) => sum + Number(s.total_amount || 0), 0);
      setUnsettledCod(codSum);
      setUnsettledPay(paySum);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  const net = unsettledCod - unsettledPay;
  const netLabel = net >= 0 ? t.financials.driver_owes_carrier : t.financials.carrier_owes_driver;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="h-4 w-4" />
          {t.financials.driver_balance}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900 p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownCircle className="h-4 w-4 text-red-600" />
              <span className="text-xs text-red-700 dark:text-red-300">{t.financials.unsettled_cod}</span>
            </div>
            {loading ? (
              <Skeleton className="h-7 w-32" />
            ) : (
              <div className="text-xl font-bold text-red-700 dark:text-red-300">
                {formatCurrency(unsettledCod)}
              </div>
            )}
          </div>
          <div className="rounded-lg border bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-900 p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpCircle className="h-4 w-4 text-green-600" />
              <span className="text-xs text-green-700 dark:text-green-300">{t.financials.unsettled_pay}</span>
            </div>
            {loading ? (
              <Skeleton className="h-7 w-32" />
            ) : (
              <div className="text-xl font-bold text-green-700 dark:text-green-300">
                {formatCurrency(unsettledPay)}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.financials.net}</span>
            <span className="text-xs text-muted-foreground">{netLabel}</span>
          </div>
          {loading ? (
            <Skeleton className="h-7 w-32" />
          ) : (
            <span
              className={cn(
                'text-xl font-bold',
                net > 0 && 'text-red-600',
                net < 0 && 'text-green-600',
                net === 0 && 'text-muted-foreground'
              )}
            >
              {formatCurrency(Math.abs(net))}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
